import { chatActions } from '../../actions/chat/chatActions.js';

const INITIAL_STATE = {
  conversations: [],
  activeConversationId: null,
  messages: {},
  fetching: {
    conversations: false,
    messages: false,
    sendMessage: false
  },
  error: null,
  isOpen: false,
  unreadCount: 0
};

// Helper function to compare message arrays
const messagesHaveChanged = (oldMessages, newMessages) => {
  if (!oldMessages || !newMessages) return true;
  if (oldMessages.length !== newMessages.length) return true;
  
  // Deep comparison of messages to detect changes
  for (let i = 0; i < newMessages.length; i++) {
    const oldMsg = oldMessages[i];
    const newMsg = newMessages[i];
    
    // Compare key message properties (use camelCase to match backend)
    if (
      oldMsg?.id !== newMsg?.id ||
      oldMsg?.uuid !== newMsg?.uuid ||
      oldMsg?.message !== newMsg?.message ||
      oldMsg?.createdAt !== newMsg?.createdAt ||
      oldMsg?.senderId !== newMsg?.senderId ||
      oldMsg?.isOwn !== newMsg?.isOwn ||
      oldMsg?.isRead !== newMsg?.isRead
    ) {
      return true;
    }
  }
  
  return false;
};

// Helper function to compare conversation arrays
const conversationsHaveChanged = (oldConversations, newConversations) => {
  if (!oldConversations || !newConversations) return true;
  if (oldConversations.length !== newConversations.length) return true;
  
  // Compare key conversation properties
  for (let i = 0; i < newConversations.length; i++) {
    const oldConv = oldConversations[i];
    const newConv = newConversations[i];
    
    if (
      oldConv?.id !== newConv?.id ||
      oldConv?.lastMessage !== newConv?.lastMessage ||
      oldConv?.unreadCount !== newConv?.unreadCount
    ) {
      return true;
    }
  }
  
  return false;
};

const chatReducers = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    // Get Conversations
    case chatActions.CHAT_CONVERSATIONS_REQUEST:
      return {
        ...state,
        fetching: { ...state.fetching, conversations: true }
      };
    case chatActions.CHAT_CONVERSATIONS_SUCCESS:
      // Only update conversations if they have actually changed
      if (!conversationsHaveChanged(state.conversations, action.conversations)) {
        // No changes, keep state as is but still mark fetching as false
        return {
          ...state,
          fetching: { ...state.fetching, conversations: false }
        };
      }
      
      // Calculate total unread count from all conversations
      const totalUnread = action.conversations.reduce((sum, conv) => sum + (conv.unreadCount || 0), 0);
      
      return {
        ...state,
        conversations: action.conversations,
        unreadCount: totalUnread,
        fetching: { ...state.fetching, conversations: false },
        error: null
      };
    case chatActions.CHAT_CONVERSATIONS_FAILURE:
      return {
        ...state,
        fetching: { ...state.fetching, conversations: false },
        error: action.error
      };

    // Get Messages
    case chatActions.CHAT_MESSAGES_REQUEST:
      return {
        ...state,
        fetching: { ...state.fetching, messages: true }
      };
    case chatActions.CHAT_MESSAGES_SUCCESS:
      // Only update messages if they have actually changed
      const oldMessages = state.messages[action.conversationId];
      if (!messagesHaveChanged(oldMessages, action.messages)) {
        // No changes, keep state as is but still mark fetching as false
        return {
          ...state,
          fetching: { ...state.fetching, messages: false }
        };
      }
      return {
        ...state,
        messages: {
          ...state.messages,
          [action.conversationId]: action.messages
        },
        fetching: { ...state.fetching, messages: false },
        error: null
      };
    case chatActions.CHAT_MESSAGES_FAILURE:
      return {
        ...state,
        fetching: { ...state.fetching, messages: false },
        error: action.error
      };

    // Send Message
    case chatActions.CHAT_SEND_MESSAGE_REQUEST:
      return {
        ...state,
        fetching: { ...state.fetching, sendMessage: true }
      };
    case chatActions.CHAT_SEND_MESSAGE_SUCCESS:
      const conversationId = state.activeConversationId;
      return {
        ...state,
        messages: {
          ...state.messages,
          [conversationId]: [
            ...(state.messages[conversationId] || []),
            action.message
          ]
        },
        fetching: { ...state.fetching, sendMessage: false },
        error: null
      };
    case chatActions.CHAT_SEND_MESSAGE_FAILURE:
      return {
        ...state,
        fetching: { ...state.fetching, sendMessage: false },
        error: action.error
      };

    // Set Active Conversation
    case chatActions.SET_ACTIVE_CONVERSATION:
      return {
        ...state,
        activeConversationId: action.conversationId
      };

    // Toggle Chat Open
    case chatActions.TOGGLE_CHAT_OPEN:
      return {
        ...state,
        isOpen: action.isOpen
      };

    // Set Unread Count
    case chatActions.SET_UNREAD_COUNT:
      return {
        ...state,
        unreadCount: action.count
      };

    default:
      return state;
  }
};

export default chatReducers;
